import { CallState, LoadingState } from '@lcs/shared/api';
import { EffectiveReports } from '@ndt/reports/shared/effective-reports/effective-reports';
import { createReducer, on } from '@ngrx/store';
import { Privileges } from 'src/app/privileges/privileges';
import * as AppStartAction from '../../app-start/app-start.actions';
import { CurrentUser } from '../models/current-user.interface';
import * as AuthenticationActions from './authentication.actions';
import { updateIsSystemUser } from './authentication.actions';

export const authenticationFeatureKey = 'authentication';

export interface AuthenticationState {
   applicationVersion: string | null;
   buildTimestamp: Date | null;
   currentUser: CurrentUser | null;
   loginCallState: CallState;
   logoutCallState: CallState;
   ndtVersion: string | null;
   privileges: Map<number, Privileges[]>;
   effectiveReports: EffectiveReports[];
   timeOutDate: string | null;
   tokenExpirationDate: string | null;
   loginError: string | null;
}

export const initialState: AuthenticationState = {
   applicationVersion: null,
   buildTimestamp: null,
   currentUser: null,
   loginCallState: LoadingState.Init,
   loginError: null,
   logoutCallState: LoadingState.Init,
   ndtVersion: null,
   effectiveReports: [],
   privileges: new Map<number, Privileges[]>(),
   timeOutDate: null,
   tokenExpirationDate: null,
};

export const reducer = createReducer(
   initialState,

   on(AuthenticationActions.login, AuthenticationActions.reAuthenticate, (state) => {
      return {
         ...state,
         loginCallState: LoadingState.Loading,
         token: null,
      };
   }),
   on(
      AuthenticationActions.loginSuccess,
      AuthenticationActions.reAuthenticationSuccess,
      AppStartAction.authenticationVerificationSuccess,
      (state, { loginResult }) => {
         const userPrivileges: Map<number, Privileges[]> = new Map(
            Object.entries(loginResult.UserPrivileges).map(([key, value]) => {
               return [+key, value];
            })
         );
         return {
            ...state,
            applicationVersion: loginResult.ApplicationVersion,
            buildTimestamp: loginResult.BuildTimestamp,
            currentUser: {
               emailAddress: loginResult.EmailAddress,
               ipAddress: loginResult.IpAddress,
               isAdmin: loginResult.IsAdmin,
               isLocal: loginResult.IsLocal,
               isSystemUser: loginResult.IsSystemUser,
               isTrueSystemUser: loginResult.IsSystemUser,
               name: loginResult.Name,
               profileImage: loginResult.ProfileImageToken,
               userAccount: {
                  accountId: loginResult.AccountId,
                  name: loginResult.AccountName,
               },
               userId: loginResult.UserId,
               username: loginResult.Username,
               shortname: loginResult.Shortname,
               extension: loginResult.Extension
                  ? {
                       extensionId: loginResult.Extension.ExtensionId,
                       name: null,
                       number: loginResult.Extension.Number,
                    }
                  : null,
            },
            loginCallState: LoadingState.Loaded,
            loginError: null,
            ndtVersion: loginResult.NdtVersion,
            privileges: userPrivileges,
            effectiveReports: loginResult.UserReports,
            tokenExpirationDate: loginResult.ExpirationDate,
         };
      }
   ),
   on(AuthenticationActions.loginFailure, AuthenticationActions.reAuthenticationFailure, (state, error) => {
      return {
         ...state,
         loginCallState: { error },
         loginError: error.UserMessage,
      };
   }),
   on(AuthenticationActions.logout, (state) => {
      return {
         ...state,
         logoutCallState: LoadingState.Loading,
      };
   }),
   on(AuthenticationActions.apiLogoutSuccess, AuthenticationActions.reAuthenticationCancel, (state) => {
      return {
         ...state,
         currentUser: null,
         loginCallState: LoadingState.Init,
         loginError: null,
         logoutCallState: LoadingState.Loaded,
      };
   }),
   on(AuthenticationActions.apiLogoutFailure, (state, action) => {
      return {
         ...state,
         loginCallState: LoadingState.Init,
         logoutCallState: { error: action },
      };
   }),
   on(AuthenticationActions.ssoLoginSuccess, (state) => {
      return {
         ...state,
         loginCallState: LoadingState.Loading,
      };
   }),
   on(AuthenticationActions.ssoLoginTimeout, (state) => {
      return {
         ...state,
         loginError: 'Login failed: Session timeout',
      };
   }),
   on(AuthenticationActions.ssoLoginFailure, (state) => {
      return {
         ...state,
         loginError: 'Login failed',
      };
   }),
   on(AuthenticationActions.reAuthenticationRequired, (state) => {
      return {
         ...state,
         loginCallState: LoadingState.Init,
         tokenExpirationDate: null,
      };
   }),
   on(updateIsSystemUser, (state, { isSystemUser }) => {
      return {
         ...state,
         currentUser: state.currentUser ? { ...state.currentUser, isSystemUser } : null,
      };
   })
);
